import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { UsersThree, Plus, House, ArrowRight } from "@phosphor-icons/react";
import HelpLink from "@/components/HelpLink";

const ROLE_LABEL = {
  owner: "Owner",
  editor: "Editor",
  viewer: "Viewer",
  executor: "Executor",
};

export default function Households() {
  const { households, refreshHouseholds, setActiveLedger, activeLedgerId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    refreshHouseholds().finally(() => setLoading(false));
  }, [refreshHouseholds]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const r = await api.post("/households", { name: name.trim() });
      toast.success("Household created.");
      setName("");
      await refreshHouseholds();
      setActiveLedger(r.data.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create household.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid="households-page" className="space-y-8">
      <header className="flex items-end justify-between border-b border-[#C9C4B8] pb-6">
        <div>
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2 flex items-center gap-3">
          <span>Households · Multi-User Ledgers</span>
          <HelpLink anchor="households" />
        </div>
          <h1 className="font-serif-display text-5xl text-[#0A192F] leading-none">Shared records.</h1>
          <p className="text-sm text-[#5C5F58] mt-3 max-w-xl">
            A household ledger is a separate book — invite a spouse, executor, or trusted heir with a
            specific role. Their writes appear here, never in your personal record.
          </p>
        </div>
      </header>

      {/* Create new household */}
      <section className="border border-[#E0DDD6] bg-white p-6">
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-3">
          Establish a Household Ledger
        </div>
        <form onSubmit={onCreate} className="flex flex-col sm:flex-row gap-3" data-testid="create-household-form">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. The Hawthorne Estate"
            data-testid="create-household-name-input"
            className="flex-1 border border-[#E0DDD6] focus:border-[#1A3622] focus:outline-none px-4 py-2.5 text-sm bg-white"
            maxLength={120}
          />
          <button
            type="submit"
            disabled={creating || !name.trim()}
            data-testid="create-household-submit"
            className="inline-flex items-center justify-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50 transition-colors"
          >
            <Plus size={14} weight="bold" />
            {creating ? "Establishing…" : "Establish household"}
          </button>
        </form>
      </section>

      {/* List */}
      <section className="space-y-3">
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
          Your Households
        </div>

        {loading ? (
          <div className="text-sm text-[#5C5F58]">Loading…</div>
        ) : households.length === 0 ? (
          <div
            data-testid="households-empty"
            className="border border-dashed border-[#C9C4B8] bg-[#F9F8F6] p-8 text-center"
          >
            <UsersThree size={32} weight="thin" className="mx-auto text-[#5C5F58] mb-3" />
            <p className="font-serif-display text-xl text-[#0A192F]">
              You haven&apos;t created a household yet.
            </p>
            <p className="text-sm text-[#5C5F58] mt-1">
              Your personal ledger will continue to be private. Create a household to share with others.
            </p>
          </div>
        ) : (
          <ul className="grid gap-3">
            {households.map((h) => {
              const isActive = h.id === activeLedgerId;
              return (
                <li
                  key={h.id}
                  data-testid={`household-row-${h.id}`}
                  className="border border-[#E0DDD6] bg-white p-5 flex items-center justify-between gap-4"
                >
                  <div className="min-w-0 flex-1 flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#1A3622]/10 border border-[#1A3622]/20 flex items-center justify-center shrink-0">
                      <House size={18} weight="regular" className="text-[#1A3622]" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-serif-display text-xl text-[#0A192F] truncate">{h.name}</div>
                      <div className="text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58] mt-1">
                        {ROLE_LABEL[h.my_role] || h.my_role} · Created {fmtDate(h.created_at)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {isActive ? (
                      <span
                        data-testid={`household-active-badge-${h.id}`}
                        className="px-2 py-0.5 bg-[#1A3622] text-[#F9F8F6] text-[10px] font-mono-ledger uppercase tracking-[0.2em]"
                      >
                        Active
                      </span>
                    ) : (
                      <button
                        onClick={() => {
                          setActiveLedger(h.id);
                          toast.success(`Switched to ${h.name}.`);
                        }}
                        data-testid={`household-activate-${h.id}`}
                        className="text-xs text-[#1A3622] hover:underline"
                      >
                        Switch to
                      </button>
                    )}
                    <Link
                      to={`/households/${h.id}`}
                      data-testid={`household-manage-${h.id}`}
                      className="inline-flex items-center gap-1 border border-[#E0DDD6] hover:border-[#1A3622] px-3 py-1.5 text-xs text-[#0A192F] transition-colors"
                    >
                      Manage
                      <ArrowRight size={12} weight="bold" />
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
